import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Button,
  Space,
  Typography,
  message,
  QRCode,
  Tooltip,
} from "antd";
import { useUserInfo } from "../../context/ThemeContext";
import { useCommonData } from "../../layout/ELayout";
import SettingModal from "../setting/SettingModal";
import domToImage from "dom-to-image";
import copy from "copy-to-clipboard";
import styles from "./pointsShareModal.module.scss";
import http from "../../http";

const { Title, Paragraph, Text } = Typography;

export default function PointsShareModal(props: {
  open: boolean;
  onClose: () => void;
}) {
  const { open, onClose } = props;
  const { userInfo } = useUserInfo();
  const { commonData } = useCommonData();
  const posterRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [qrStatus, setQrStatus] = useState<any>("loading");
  const [shareLink, setShareLink] = useState("");

  useEffect(() => {
    if (open && userInfo?.uuid) {
      setQrStatus("loading");
      http
        .post(`/api/v1/invite/code?inviter_id=${userInfo.uuid}`)
        .then((res: any) => {
          let code = "";
          if (res?.status) {
            code = res?.data?.invite_code ?? res?.data ?? "";
          }
          if (code) {
            setInviteCode(`${code}`);
            setShareLink(`${window.location.origin}/?invite_code=${code}`);
            setQrStatus("active");
          } else {
            setQrStatus("expired");
          }
        })
        .catch(() => setQrStatus("expired"));
    }
  }, [open, userInfo?.uuid]);

  const handleCopy = () => {
    if (!shareLink) return;
    const ok = copy(shareLink);
    ok ? message.success("邀请链接已复制") : message.error("复制失败");
  };

  const handleDownloadPoster = async () => {
    const node = posterRef.current;
    if (!node) return;
    try {
      setDownloading(true);
      const scale = Math.max(3, (window as any).devicePixelRatio || 2);
      const blob = await domToImage.toBlob(node, {
        cacheBust: true,
        bgcolor: "#ffffff",
        width: node.offsetWidth * scale,
        height: node.offsetHeight * scale,
        style: {
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        },
      });

      // 触发下载
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `eagle-invite-${Date.now()}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);

      // 写入系统剪贴板（浏览器支持且已授权）
      let clipboardOk = false;
      try {
        const ClipboardItemCtor: any = (window as any).ClipboardItem || ClipboardItem;
        if (navigator.clipboard && ClipboardItemCtor) {
          const item = new ClipboardItemCtor({ [blob.type]: blob });
          await navigator.clipboard.write([item]);
          clipboardOk = true;
        }
      } catch (e) {
        clipboardOk = false;
      }

      if (clipboardOk) {
        message.success("海报已下载，并已复制到粘贴板");
      } else {
        message.success("海报已下载；复制到粘贴板未成功，可能需HTTPS或授权");
      }
    } catch (err) {
      message.error("海报生成失败，请重试");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      title="分享邀请赚积分"
      open={open}
      onCancel={onClose}
      footer={null}
      maskClosable={false}
      centered
      width={800}
    >
      <div className={styles.container}>
        <div ref={posterRef} className={styles.poster}>
          <div className={styles.brand}></div>
          <div className={styles.qr}>
            <QRCode
              status={qrStatus}
              value={shareLink}
              bordered={false}
              size={60}
              errorLevel="H"
              type="svg"
            />
          </div>
        </div>
        <div className={styles.side}>
          <div className={styles.stats}>
            <Text>当前积分余额：</Text>
            <Text strong>{userInfo?.points ?? 0}</Text>
            <br />
            <Text>免费次数：</Text>
            <Text strong>{commonData?.freeTimes ?? 0}</Text>
          </div>
          <Paragraph>
            如果您的积分不足或试用次数已用完。可以通过分享邀请新用户注册来获取额外10积分，或前往充值。
          </Paragraph>
          <Space direction="vertical" className={styles.actions}>
            <Tooltip
              title={
                <span>
                  邀请链接：
                  {shareLink}
                </span>
              }
            >
              <Button onClick={handleCopy} block>
                复制邀请链接
              </Button>
            </Tooltip>
            <Button onClick={handleDownloadPoster} loading={downloading} block>
              保存分享海报
            </Button>
            <SettingModal tabKey="pay">
              <Button type="primary" block>
                去充值
              </Button>
            </SettingModal>
            <Button onClick={onClose} block>
              关闭
            </Button>
          </Space>
        </div>
      </div>
    </Modal>
  );
}
